import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Bath, 
  ArrowRight, 
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  Upload,
  MapPin,
  Calculator,
  Calendar,
  Info,
  Trash2,
  Move,
  ShowerHead,
  Droplets,
  Lightbulb,
  Wrench,
  PaintBucket,
  Thermometer,
  Grid3X3,
  Layers,
  Square,
  CircleDot,
  Plus,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bathroom layouts configuration
const BATHROOM_LAYOUTS = [
  {
    id: "compact-ensuite",
    name: "Compact Ensuite",
    description: "Small bathroom with toilet, basin and shower enclosure",
    floorArea: { min: 3, max: 4, default: 3.5 },
    wallArea: { min: 12, max: 16, default: 14 },
    fixtures: ["toilet", "basin", "shower_enclosure"],
    plumbing: "minor",
    icon: "🚿"
  },
  {
    id: "standard-bathroom",
    name: "Standard Bathroom",
    description: "Common family bathroom with bath, shower over bath, toilet and basin",
    floorArea: { min: 4, max: 6, default: 5 },
    wallArea: { min: 18, max: 24, default: 20 },
    fixtures: ["toilet", "basin", "standard_bath", "shower_over_bath"],
    plumbing: "minor",
    icon: "🛁"
  },
  {
    id: "bath-separate-shower",
    name: "Bath & Separate Shower",
    description: "Medium bathroom with separate bath and shower enclosure",
    floorArea: { min: 6, max: 8, default: 7 },
    wallArea: { min: 24, max: 32, default: 28 },
    fixtures: ["toilet", "basin", "standard_bath", "shower_enclosure"],
    plumbing: "minor",
    icon: "🛁🚿"
  },
  {
    id: "walk-in-shower",
    name: "Walk-In Shower Bathroom",
    description: "Modern bathroom with walk-in shower, no bath",
    floorArea: { min: 4, max: 7, default: 5.5 },
    wallArea: { min: 16, max: 28, default: 22 },
    fixtures: ["toilet", "basin", "walk_in_shower"],
    plumbing: "minor",
    icon: "🚿"
  },
  {
    id: "wet-room",
    name: "Wet Room",
    description: "Fully waterproofed bathroom with open shower area",
    floorArea: { min: 4, max: 8, default: 6 },
    wallArea: { min: 16, max: 32, default: 24 },
    fixtures: ["toilet", "basin", "wet_room_shower"],
    plumbing: "minor",
    extras: ["waterproof_tanking", "floor_gradient"],
    icon: "💧"
  },
  {
    id: "luxury-bathroom",
    name: "Luxury Bathroom",
    description: "Large bathroom with freestanding bath, walk-in shower and double vanity",
    floorArea: { min: 8, max: 12, default: 10 },
    wallArea: { min: 32, max: 48, default: 40 },
    fixtures: ["toilet", "double_basin_vanity", "freestanding_bath", "walk_in_shower"],
    plumbing: "major",
    icon: "✨"
  },
  {
    id: "custom",
    name: "Custom Layout",
    description: "Design your own bathroom configuration",
    floorArea: { min: 3, max: 15, default: 6 },
    wallArea: { min: 12, max: 60, default: 24 },
    fixtures: [],
    plumbing: "none",
    icon: "🎨"
  }
];

// All scope sections
const SCOPE_SECTIONS = {
  removal: {
    title: "Existing Bathroom Removal",
    icon: Trash2,
    items: [
      { id: "full_strip_out", label: "Full bathroom strip out", price: { min: 300, max: 600 } },
      { id: "remove_wall_tiles", label: "Remove wall tiles", price: { min: 150, max: 300 } },
      { id: "remove_floor_tiles", label: "Remove floor tiles", price: { min: 100, max: 200 } },
      { id: "remove_flooring", label: "Remove existing flooring", price: { min: 80, max: 150 } },
      { id: "remove_toilet", label: "Remove toilet", price: { min: 40, max: 80 } },
      { id: "remove_basin", label: "Remove basin", price: { min: 30, max: 60 } },
      { id: "remove_vanity", label: "Remove vanity unit", price: { min: 50, max: 100 } },
      { id: "remove_bath", label: "Remove bath", price: { min: 60, max: 120 } },
      { id: "remove_shower_enclosure", label: "Remove shower enclosure", price: { min: 80, max: 150 } },
      { id: "remove_shower_tray", label: "Remove shower tray", price: { min: 50, max: 100 } },
      { id: "remove_shower_screen", label: "Remove shower screens", price: { min: 30, max: 60 } },
      { id: "remove_bidet", label: "Remove bidet", price: { min: 40, max: 80 } },
      { id: "remove_radiator", label: "Remove radiator", price: { min: 40, max: 80 } },
      { id: "remove_towel_rail", label: "Remove heated towel rail", price: { min: 30, max: 60 } },
      { id: "remove_ceiling_panels", label: "Remove ceiling panels", price: { min: 60, max: 120 } },
      { id: "remove_lighting", label: "Remove lighting fixtures", price: { min: 40, max: 80 } },
      { id: "remove_mirror_cabinet", label: "Remove mirror cabinet", price: { min: 30, max: 60 } },
      { id: "remove_storage", label: "Remove storage cabinets", price: { min: 40, max: 80 } },
      { id: "remove_boxing", label: "Remove boxing/pipe covers", price: { min: 50, max: 100 } },
      { id: "waste_removal", label: "Waste removal required", price: { min: 100, max: 250 } },
      { id: "skip_required", label: "Skip hire required", price: { min: 200, max: 350 } }
    ]
  },
  plumbing: {
    title: "Plumbing Work",
    icon: Droplets,
    items: [
      { id: "move_toilet_waste", label: "Move toilet waste", price: { min: 200, max: 400 } },
      { id: "move_basin_waste", label: "Move basin waste", price: { min: 100, max: 200 } },
      { id: "move_bath_waste", label: "Move bath waste", price: { min: 150, max: 300 } },
      { id: "move_shower_waste", label: "Move shower waste", price: { min: 150, max: 300 } },
      { id: "new_hot_cold_feeds", label: "Install new hot and cold feeds", price: { min: 200, max: 400 } },
      { id: "replace_pipework", label: "Replace old pipework", price: { min: 300, max: 600 } },
      { id: "isolation_valves", label: "Install isolation valves", price: { min: 50, max: 100 } },
      { id: "pressure_valve", label: "Install pressure reducing valve", price: { min: 100, max: 200 } },
      { id: "new_stop_tap", label: "Install new stop tap", price: { min: 80, max: 150 } },
      { id: "upgrade_pipework", label: "Upgrade pipework system", price: { min: 400, max: 800 } }
    ]
  },
  firstFixPlumbing: {
    title: "First Fix Plumbing",
    icon: Wrench,
    items: [
      { id: "bath_connections", label: "Install bath water connections", price: { min: 100, max: 200 } },
      { id: "basin_connections", label: "Install basin water connections", price: { min: 80, max: 150 } },
      { id: "shower_connections", label: "Install shower connections", price: { min: 100, max: 200 } },
      { id: "bidet_connections", label: "Install bidet connections", price: { min: 80, max: 150 } },
      { id: "washing_machine_connection", label: "Install washing machine connection", price: { min: 100, max: 200 } },
      { id: "new_waste_pipes", label: "Install new waste pipes", price: { min: 150, max: 300 } },
      { id: "concealed_pipework", label: "Install concealed pipework", price: { min: 200, max: 400 } }
    ]
  },
  electrical: {
    title: "Electrical Work",
    icon: Lightbulb,
    items: [
      { id: "ceiling_light", label: "Install ceiling light", price: { min: 60, max: 120 } },
      { id: "led_downlights", label: "Install LED downlights", price: { min: 150, max: 300 } },
      { id: "wall_lighting", label: "Install wall lighting", price: { min: 80, max: 150 } },
      { id: "mirror_lighting", label: "Install mirror lighting", price: { min: 60, max: 120 } },
      { id: "led_strip_lighting", label: "Install LED strip lighting", price: { min: 100, max: 200 } },
      { id: "illuminated_mirror", label: "Install illuminated mirror", price: { min: 80, max: 150 } },
      { id: "led_mirror_cabinet", label: "Install LED mirror cabinet", price: { min: 100, max: 200 } },
      { id: "electric_ufh", label: "Install electric underfloor heating", price: { min: 400, max: 800 } },
      { id: "towel_rail_wiring", label: "Install heated towel rail wiring", price: { min: 100, max: 200 } },
      { id: "extractor_fan", label: "Install extractor fan", price: { min: 80, max: 150 } },
      { id: "humidity_extractor", label: "Install humidity controlled extractor", price: { min: 120, max: 220 } },
      { id: "upgrade_extractor", label: "Upgrade extractor fan", price: { min: 60, max: 120 } },
      { id: "shaver_socket", label: "Install shaver socket", price: { min: 60, max: 120 } },
      { id: "waterproof_socket", label: "Install waterproof socket", price: { min: 80, max: 150 } }
    ]
  },
  waterproofing: {
    title: "Waterproofing & Preparation",
    icon: Layers,
    items: [
      { id: "tile_backer_boards", label: "Install tile backer boards", price: { min: 200, max: 400 } },
      { id: "cement_boards", label: "Install cement boards", price: { min: 150, max: 300 } },
      { id: "tanking_system", label: "Install waterproof tanking system", price: { min: 300, max: 600 } },
      { id: "waterproof_membrane", label: "Install waterproof membrane", price: { min: 150, max: 300 } },
      { id: "self_levelling", label: "Self levelling floor compound", price: { min: 150, max: 300 } },
      { id: "replace_subfloor", label: "Replace subfloor", price: { min: 200, max: 400 } },
      { id: "new_floorboards", label: "Install new floor boards", price: { min: 150, max: 300 } },
      { id: "insulation_boards", label: "Install insulation boards", price: { min: 100, max: 200 } }
    ]
  },
  wallFinishes: {
    title: "Wall Tiling",
    icon: Grid3X3,
    options: [
      { id: "half_wall", label: "Half wall tiling", multiplier: 0.5 },
      { id: "full_wall", label: "Full wall tiling", multiplier: 0.75 },
      { id: "full_height", label: "Full height tiling", multiplier: 1.0 },
      { id: "feature_wall", label: "Feature wall only", multiplier: 0.25 }
    ],
    tileTypes: [
      { id: "ceramic", label: "Ceramic tiles", price: { min: 45, max: 65 } },
      { id: "porcelain", label: "Porcelain tiles", price: { min: 55, max: 80 } },
      { id: "natural_stone", label: "Natural stone tiles", price: { min: 70, max: 120 } },
      { id: "large_format", label: "Large format tiles", price: { min: 65, max: 100 } },
      { id: "mosaic", label: "Mosaic tiles", price: { min: 80, max: 150 } }
    ],
    extras: [
      { id: "tile_trim", label: "Tile trim installation", price: { min: 50, max: 100 } },
      { id: "edging_profiles", label: "Tile edging profiles", price: { min: 60, max: 120 } },
      { id: "niche_tiling", label: "Feature niche tiling", price: { min: 80, max: 150 } }
    ]
  },
  floorFinishes: {
    title: "Floor Tiling",
    icon: Square,
    options: [
      { id: "ceramic_floor", label: "Ceramic floor tiles", price: { min: 50, max: 70 } },
      { id: "porcelain_floor", label: "Porcelain floor tiles", price: { min: 60, max: 90 } },
      { id: "natural_stone_floor", label: "Natural stone floor tiles", price: { min: 80, max: 130 } },
      { id: "lvt", label: "Luxury vinyl tile (LVT)", price: { min: 40, max: 70 } },
      { id: "vinyl_click", label: "Vinyl click flooring", price: { min: 35, max: 60 } },
      { id: "waterproof_laminate", label: "Waterproof laminate", price: { min: 30, max: 55 } },
      { id: "sheet_vinyl", label: "Sheet vinyl flooring", price: { min: 25, max: 45 } }
    ],
    extras: [
      { id: "floor_ufh", label: "Underfloor heating", price: { min: 400, max: 900 } },
      { id: "floor_membrane", label: "Waterproof floor membrane", price: { min: 100, max: 200 } },
      { id: "floor_levelling", label: "Floor levelling compound", price: { min: 100, max: 200 } }
    ]
  },
  bath: {
    title: "Bath Installation",
    icon: Bath,
    options: [
      { id: "no_bath", label: "No bath", price: { min: 0, max: 0 } },
      { id: "standard_bath", label: "Standard bath", price: { min: 300, max: 600 } },
      { id: "freestanding_bath", label: "Freestanding bath", price: { min: 600, max: 1200 } },
      { id: "corner_bath", label: "Corner bath", price: { min: 400, max: 800 } },
      { id: "shower_bath", label: "Shower bath combination", price: { min: 350, max: 700 } }
    ],
    extras: [
      { id: "bath_panel", label: "Bath panel installation", price: { min: 50, max: 100 } },
      { id: "bath_screen", label: "Bath screen installation", price: { min: 80, max: 180 } },
      { id: "bath_waste", label: "Bath waste installation", price: { min: 40, max: 80 } },
      { id: "bath_taps", label: "Bath tap installation", price: { min: 60, max: 120 } }
    ]
  },
  shower: {
    title: "Shower Installation",
    icon: ShowerHead,
    options: [
      { id: "no_shower", label: "No shower", price: { min: 0, max: 0 } },
      { id: "shower_enclosure", label: "Shower enclosure", price: { min: 300, max: 600 } },
      { id: "walk_in_shower", label: "Walk-in shower", price: { min: 500, max: 1000 } },
      { id: "wet_room_shower", label: "Wet room shower", price: { min: 600, max: 1200 } }
    ],
    components: [
      { id: "shower_tray", label: "Shower tray installation", price: { min: 100, max: 200 } },
      { id: "shower_screen", label: "Shower screen installation", price: { min: 100, max: 250 } },
      { id: "linear_drain", label: "Linear drain installation", price: { min: 150, max: 300 } },
      { id: "shower_waste", label: "Shower waste installation", price: { min: 40, max: 80 } }
    ],
    systems: [
      { id: "mixer_shower", label: "Standard mixer shower", price: { min: 150, max: 300 } },
      { id: "thermostatic_shower", label: "Thermostatic shower", price: { min: 200, max: 400 } },
      { id: "rainfall_shower", label: "Rainfall shower", price: { min: 250, max: 500 } },
      { id: "digital_shower", label: "Digital shower", price: { min: 400, max: 800 } },
      { id: "dual_shower", label: "Dual shower system", price: { min: 300, max: 600 } }
    ]
  },
  basin: {
    title: "Basin Installation",
    icon: CircleDot,
    options: [
      { id: "pedestal_basin", label: "Pedestal basin", price: { min: 150, max: 300 } },
      { id: "wall_hung_basin", label: "Wall hung basin", price: { min: 200, max: 400 } },
      { id: "countertop_basin", label: "Countertop basin", price: { min: 250, max: 500 } },
      { id: "vanity_basin", label: "Vanity basin unit", price: { min: 300, max: 700 } },
      { id: "double_basin", label: "Double basin vanity", price: { min: 500, max: 1000 } }
    ],
    extras: [
      { id: "bottle_trap", label: "Bottle trap installation", price: { min: 30, max: 60 } },
      { id: "basin_waste", label: "Basin waste installation", price: { min: 25, max: 50 } },
      { id: "vanity_cabinet", label: "Vanity cabinet installation", price: { min: 100, max: 200 } }
    ]
  },
  toilet: {
    title: "Toilet Installation",
    icon: Bath,
    options: [
      { id: "standard_toilet", label: "Standard toilet", price: { min: 200, max: 400 } },
      { id: "back_to_wall", label: "Back to wall toilet", price: { min: 300, max: 550 } },
      { id: "wall_hung_toilet", label: "Wall hung toilet", price: { min: 400, max: 700 } }
    ],
    extras: [
      { id: "concealed_cistern", label: "Concealed cistern installation", price: { min: 150, max: 300 } },
      { id: "soft_close_seat", label: "Soft close toilet seat", price: { min: 40, max: 80 } },
      { id: "bidet_spray", label: "Bidet spray attachment", price: { min: 60, max: 120 } }
    ]
  },
  storage: {
    title: "Storage & Furniture",
    icon: Layers,
    items: [
      { id: "vanity_unit", label: "Vanity unit", price: { min: 200, max: 500 } },
      { id: "tall_cabinet", label: "Tall storage cabinet", price: { min: 150, max: 400 } },
      { id: "mirror_cabinet", label: "Mirror cabinet", price: { min: 100, max: 300 } },
      { id: "floating_shelves", label: "Floating shelves", price: { min: 50, max: 150 } },
      { id: "wall_niche", label: "Built-in wall niche", price: { min: 100, max: 250 } }
    ]
  },
  heating: {
    title: "Heating",
    icon: Thermometer,
    items: [
      { id: "radiator", label: "Radiator installation", price: { min: 150, max: 300 } },
      { id: "heated_towel_rail", label: "Heated towel rail", price: { min: 200, max: 400 } },
      { id: "electric_towel_rail", label: "Electric heated towel rail", price: { min: 150, max: 300 } },
      { id: "ufh_system", label: "Underfloor heating system", price: { min: 500, max: 1200 } }
    ]
  },
  ceiling: {
    title: "Ceiling Work",
    icon: Layers,
    items: [
      { id: "plasterboard_ceiling", label: "Install new plasterboard ceiling", price: { min: 200, max: 400 } },
      { id: "pvc_panels", label: "Install PVC ceiling panels", price: { min: 150, max: 300 } },
      { id: "spotlights", label: "Install spotlights", price: { min: 150, max: 300 } },
      { id: "led_ceiling", label: "Install LED ceiling lighting", price: { min: 100, max: 200 } }
    ]
  },
  finishing: {
    title: "Finishing Work",
    icon: PaintBucket,
    items: [
      { id: "silicone_sealing", label: "Silicone sealing", price: { min: 50, max: 100 } },
      { id: "grouting", label: "Grouting", price: { min: 80, max: 150 } },
      { id: "paint_ceiling", label: "Painting ceiling", price: { min: 60, max: 120 } },
      { id: "paint_walls", label: "Painting walls", price: { min: 80, max: 160 } },
      { id: "final_plumbing", label: "Final plumbing connections", price: { min: 100, max: 200 } },
      { id: "final_electrical", label: "Final electrical connections", price: { min: 80, max: 150 } }
    ]
  },
  extras: {
    title: "Optional Extras",
    icon: Plus,
    items: [
      { id: "led_niche", label: "LED shower niche", price: { min: 100, max: 200 } },
      { id: "smart_mirror", label: "Smart mirror", price: { min: 200, max: 500 } },
      { id: "bluetooth_mirror", label: "Bluetooth mirror", price: { min: 150, max: 350 } },
      { id: "heated_mirror", label: "Heated mirror", price: { min: 100, max: 250 } },
      { id: "stone_shelving", label: "Stone shelving", price: { min: 100, max: 250 } },
      { id: "waterproof_lighting", label: "Waterproof lighting", price: { min: 80, max: 180 } },
      { id: "custom_patterns", label: "Custom tile patterns", price: { min: 150, max: 400 } }
    ]
  }
};

const BathroomRefitPage = () => {
  const navigate = useNavigate();
  const [estimatorMode, setEstimatorMode] = useState("detailed"); // "simple" or "detailed"
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const pageTopRef = useCallback(node => {
    if (node !== null) {
      // Store ref for scrolling
      window.__bathroomRefitPageTop = node;
    }
  }, []);
  
  // Simple Quote state
  const [simpleQuote, setSimpleQuote] = useState({
    roomSize: "medium",
    refitType: "full",
    wallTiling: true,
    floorType: "tile",
    electricalWork: false,
    name: "",
    email: "",
    phone: "",
    postcode: "",
    notes: "",
    preferredDate: ""
  });
  const [simpleEstimate, setSimpleEstimate] = useState(null);
  const [simpleStep, setSimpleStep] = useState(0); // 0: options, 1: contact, 2: result
  
  // Robust scroll to top function
  const scrollToPageTop = useCallback(() => {
    // Method 1: Use the ref if available
    if (window.__bathroomRefitPageTop) {
      window.__bathroomRefitPageTop.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
    
    // Method 2: Force scroll all scroll containers
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Method 3: Find and scroll any scrollable parent
    const scrollableParent = document.querySelector('main') || document.querySelector('.App');
    if (scrollableParent) {
      scrollableParent.scrollTop = 0;
    }
  }, []);
  
  // Scroll to top when step changes
  useEffect(() => {
    // Immediate scroll
    scrollToPageTop();
    
    // Multiple attempts with different timings to ensure scroll sticks
    const timers = [
      setTimeout(scrollToPageTop, 0),
      setTimeout(scrollToPageTop, 16),  // One frame
      setTimeout(scrollToPageTop, 50),
      setTimeout(scrollToPageTop, 100),
      setTimeout(scrollToPageTop, 200)
    ];
    
    // Also use requestAnimationFrame for smoother timing
    let rafId = requestAnimationFrame(() => {
      scrollToPageTop();
      rafId = requestAnimationFrame(scrollToPageTop);
    });
    
    return () => {
      timers.forEach(t => clearTimeout(t));
      cancelAnimationFrame(rafId);
    };
  }, [currentStep, simpleStep, scrollToPageTop]);
  
  // Get simple quote estimate
  const getSimpleEstimate = async () => {
    try {
      const response = await axios.post(`${API}/simple-quote/estimate`, {
        room_size: simpleQuote.roomSize,
        refit_type: simpleQuote.refitType,
        wall_tiling: simpleQuote.wallTiling,
        floor_type: simpleQuote.floorType,
        electrical_work: simpleQuote.electricalWork
      });
      setSimpleEstimate(response.data);
    } catch (error) {
      console.error("Error getting simple estimate:", error);
      toast.error("Failed to calculate estimate");
    }
  };
  
  // Submit simple quote
  const submitSimpleQuote = async () => {
    if (!simpleQuote.name || !simpleQuote.email || !simpleQuote.phone) {
      toast.error("Please fill in your contact details");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/simple-quote/submit`, {
        room_size: simpleQuote.roomSize,
        refit_type: simpleQuote.refitType,
        wall_tiling: simpleQuote.wallTiling,
        floor_type: simpleQuote.floorType,
        electrical_work: simpleQuote.electricalWork,
        name: simpleQuote.name,
        email: simpleQuote.email,
        phone: simpleQuote.phone,
        postcode: simpleQuote.postcode,
        notes: simpleQuote.notes,
        preferred_date: simpleQuote.preferredDate
      });
      
      setSimpleEstimate(response.data);
      setSimpleStep(2);
      toast.success("Quote submitted successfully!");
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Failed to submit quote. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Render Simple Quote Mode
  const renderSimpleQuote = () => {
    if (simpleStep === 2 && simpleEstimate) {
      // Result screen
      return (
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Quote Request Submitted!</h2>
            <p className="text-gray-600 mb-6">Thank you, {simpleQuote.name}. We've received your request.</p>
            
            <div className="bg-primary/5 rounded-xl p-6 mb-6">
              <p className="text-sm text-gray-600 mb-2">Your Estimated Range</p>
              <p className="text-3xl font-bold text-primary">
                £{simpleEstimate.estimated_min?.toLocaleString()} - £{simpleEstimate.estimated_max?.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This is an indicative range based on limited information.
              </p>
            </div>
            
            <p className="text-gray-600 mb-6">
              A member of our team will be in touch shortly to discuss your project in more detail and provide a more accurate quote.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/">
                <Button variant="outline">Return Home</Button>
              </Link>
              <Link to="/book">
                <Button className="bg-primary hover:bg-primary/90">
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Site Visit
                </Button>
              </Link>
            </div>
          </div>
        </div>
      );
    }
    
    if (simpleStep === 1) {
      // Contact details step
      return (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Your Estimated Range</h2>
            {simpleEstimate && (
              <div className="bg-primary/5 rounded-xl p-4 mb-6">
                <p className="text-2xl font-bold text-primary">
                  £{simpleEstimate.min_estimate?.toLocaleString()} - £{simpleEstimate.max_estimate?.toLocaleString()}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Based on your selections. A detailed quote will provide more accurate pricing.
                </p>
              </div>
            )}
            
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Enter Your Details</h3>
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    placeholder="Your name"
                    value={simpleQuote.name}
                    onChange={(e) => setSimpleQuote(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="simple-quote-name"
                  />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input
                    placeholder="07xxx xxxxxx"
                    value={simpleQuote.phone}
                    onChange={(e) => setSimpleQuote(prev => ({ ...prev, phone: e.target.value }))}
                    data-testid="simple-quote-phone"
                  />
                </div>
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={simpleQuote.email}
                  onChange={(e) => setSimpleQuote(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="simple-quote-email"
                />
              </div>
              <div>
                <Label>Postcode</Label>
                <Input
                  placeholder="M1 1AA"
                  value={simpleQuote.postcode}
                  onChange={(e) => setSimpleQuote(prev => ({ ...prev, postcode: e.target.value }))}
                  data-testid="simple-quote-postcode"
                />
              </div>
              <div>
                <Label>Preferred Contact Date</Label>
                <Input
                  type="date"
                  value={simpleQuote.preferredDate}
                  onChange={(e) => setSimpleQuote(prev => ({ ...prev, preferredDate: e.target.value }))}
                />
              </div>
              <div>
                <Label>Additional Notes</Label>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  rows={3}
                  placeholder="Any additional details about your project..."
                  value={simpleQuote.notes}
                  onChange={(e) => setSimpleQuote(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 justify-between">
            <Button 
              variant="outline" 
              onClick={() => setSimpleStep(0)}
              data-testid="simple-quote-back"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90"
              onClick={submitSimpleQuote}
              disabled={loading}
              data-testid="simple-quote-submit"
            >
              {loading ? "Submitting..." : "Submit Quote Request"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      );
    }
    
    // Options step (step 0)
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Quick Bathroom Estimate</h2>
          
          {/* Room Size */}
          <div className="mb-6">
            <Label className="text-base font-semibold mb-3 block">Room Size</Label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: "small", label: "Small", desc: "Up to 4m²" },
                { value: "medium", label: "Medium", desc: "4-7m²" },
                { value: "large", label: "Large", desc: "7m²+" }
              ].map(size => (
                <button
                  key={size.value}
                  onClick={() => setSimpleQuote(prev => ({ ...prev, roomSize: size.value }))}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    simpleQuote.roomSize === size.value 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`simple-size-${size.value}`}
                >
                  <p className="font-semibold text-gray-900">{size.label}</p>
                  <p className="text-sm text-gray-500">{size.desc}</p>
                </button>
              ))}
            </div>
          </div>
          
          {/* Refit Type */}
          <div className="mb-6">
            <Label className="text-base font-semibold mb-3 block">Type of Work</Label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: "full", label: "Full Refit", desc: "Complete renovation" },
                { value: "partial", label: "Partial Refit", desc: "Selected updates" }
              ].map(type => (
                <button
                  key={type.value}
                  onClick={() => setSimpleQuote(prev => ({ ...prev, refitType: type.value }))}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    simpleQuote.refitType === type.value 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`simple-refit-${type.value}`}
                >
                  <p className="font-semibold text-gray-900">{type.label}</p>
                  <p className="text-sm text-gray-500">{type.desc}</p>
                </button>
              ))}
            </div>
          </div>
          
          {/* Wall Tiling */}
          <div className="mb-6">
            <Label className="text-base font-semibold mb-3 block">Wall Tiling Required?</Label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: true, label: "Yes", desc: "Include wall tiles" },
                { value: false, label: "No", desc: "Walls not included" }
              ].map(option => (
                <button
                  key={String(option.value)}
                  onClick={() => setSimpleQuote(prev => ({ ...prev, wallTiling: option.value }))}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    simpleQuote.wallTiling === option.value 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`simple-wall-${option.value}`}
                >
                  <p className="font-semibold text-gray-900">{option.label}</p>
                  <p className="text-sm text-gray-500">{option.desc}</p>
                </button>
              ))}
            </div>
          </div>
          
          {/* Floor Type */}
          <div className="mb-6">
            <Label className="text-base font-semibold mb-3 block">Floor Covering</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { value: "tile", label: "Tile" },
                { value: "vinyl", label: "Vinyl" },
                { value: "laminate", label: "Laminate" },
                { value: "other", label: "Other" }
              ].map(floor => (
                <button
                  key={floor.value}
                  onClick={() => setSimpleQuote(prev => ({ ...prev, floorType: floor.value }))}
                  className={`p-3 rounded-xl border-2 transition-all text-center ${
                    simpleQuote.floorType === floor.value 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`simple-floor-${floor.value}`}
                >
                  <p className="font-medium text-gray-900">{floor.label}</p>
                </button>
              ))}
            </div>
          </div>
          
          {/* Electrical Work */}
          <div className="mb-6">
            <Label className="text-base font-semibold mb-3 block">Electrical Work Needed?</Label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: true, label: "Yes", desc: "Include electrical" },
                { value: false, label: "No", desc: "No electrical work" }
              ].map(option => (
                <button
                  key={String(option.value)}
                  onClick={() => setSimpleQuote(prev => ({ ...prev, electricalWork: option.value }))}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    simpleQuote.electricalWork === option.value 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`simple-electrical-${option.value}`}
                >
                  <p className="font-semibold text-gray-900">{option.label}</p>
                  <p className="text-sm text-gray-500">{option.desc}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <Button
          className="w-full bg-primary hover:bg-primary/90 py-6 text-lg"
          onClick={() => {
            getSimpleEstimate();
            setSimpleStep(1);
          }}
          data-testid="simple-quote-continue"
        >
          Get My Estimate
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    );
  };
  
  // Form state
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [bathroomSize, setBathroomSize] = useState("medium");
  const [floorArea, setFloorArea] = useState(5);
  const [wallArea, setWallArea] = useState(20);
  const [wallTilingCoverage, setWallTilingCoverage] = useState("full_wall");
  const [wallTileType, setWallTileType] = useState("porcelain");
  const [floorTileType, setFloorTileType] = useState("porcelain_floor");
  const [plumbingComplexity, setPlumbingComplexity] = useState("minor");
  
  // Scope selections
  const [selectedItems, setSelectedItems] = useState({});
  
  // Bath, Shower, Basin, Toilet selections
  const [bathOption, setBathOption] = useState("no_bath");
  const [showerOption, setShowerOption] = useState("shower_enclosure");
  const [showerSystem, setShowerSystem] = useState("thermostatic_shower");
  const [basinOption, setBasinOption] = useState("vanity_basin");
  const [toiletOption, setToiletOption] = useState("standard_toilet");
  
  // Photos and contact
  const [photos, setPhotos] = useState([]);
  const [postcode, setPostcode] = useState("");
  const [contactInfo, setContactInfo] = useState({ name: "", phone: "", email: "" });
  
  // Estimate result
  const [estimate, setEstimate] = useState(null);

  const steps = [
    { title: "Layout", desc: "Choose bathroom layout" },
    { title: "Size", desc: "Bathroom dimensions" },
    { title: "Removal", desc: "Strip out work" },
    { title: "Plumbing", desc: "Plumbing requirements" },
    { title: "Electrical", desc: "Electrical work" },
    { title: "Walls", desc: "Wall tiling" },
    { title: "Floor", desc: "Floor finishes" },
    { title: "Fixtures", desc: "Bath, shower, basin" },
    { title: "Extras", desc: "Additional features" },
    { title: "Photos", desc: "Upload photos" },
    { title: "Postcode", desc: "Your location" },
    { title: "Estimate", desc: "Your quote" }
  ];

  const toggleItem = (category, itemId) => {
    setSelectedItems(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [itemId]: !prev[category]?.[itemId]
      }
    }));
  };

  const selectAllItems = (category, items) => {
    const allSelected = items.every(item => selectedItems[category]?.[item.id]);
    const newCategoryItems = {};
    items.forEach(item => {
      newCategoryItems[item.id] = !allSelected;
    });
    setSelectedItems(prev => ({
      ...prev,
      [category]: newCategoryItems
    }));
  };

  const handleLayoutSelect = (layout) => {
    setSelectedLayout(layout);
    // Pre-populate values based on layout
    setFloorArea(layout.floorArea.default);
    setWallArea(layout.wallArea.default);
    setPlumbingComplexity(layout.plumbing);
    
    // Pre-select fixtures based on layout
    const newItems = { ...selectedItems };
    layout.fixtures.forEach(f => {
      // Map fixture to appropriate section
      if (f.includes("bath")) setBathOption(f);
      if (f.includes("shower")) setShowerOption(f);
      if (f.includes("basin") || f.includes("vanity")) setBasinOption(f.includes("double") ? "double_basin" : "vanity_basin");
      if (f === "toilet") setToiletOption("standard_toilet");
    });
    setSelectedItems(newItems);
    
    // Scroll to top immediately
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    setCurrentStep(1);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length + photos.length > 5) {
      toast.error("Maximum 5 photos allowed");
      return;
    }
    
    for (const file of files) {
      const reader = new FileReader();
      reader.onload = () => {
        setPhotos(prev => [...prev, { data: reader.result, name: file.name }]);
      };
      reader.readAsDataURL(file);
    }
  };

  const calculateEstimate = async () => {
    setLoading(true);
    
    try {
      // Calculate based on selections
      let totalMin = 0;
      let totalMax = 0;
      const breakdown = {};

      // Wall tiling
      const wallTilePrice = SCOPE_SECTIONS.wallFinishes.tileTypes.find(t => t.id === wallTileType)?.price || { min: 50, max: 75 };
      const coverageMultiplier = SCOPE_SECTIONS.wallFinishes.options.find(o => o.id === wallTilingCoverage)?.multiplier || 0.75;
      const effectiveWallArea = wallArea * coverageMultiplier;
      breakdown.wall_tiling = Math.round((wallTilePrice.min + wallTilePrice.max) / 2 * effectiveWallArea);
      totalMin += wallTilePrice.min * effectiveWallArea;
      totalMax += wallTilePrice.max * effectiveWallArea;

      // Floor tiling
      const floorPrice = SCOPE_SECTIONS.floorFinishes.options.find(o => o.id === floorTileType)?.price || { min: 55, max: 85 };
      breakdown.floor_tiling = Math.round((floorPrice.min + floorPrice.max) / 2 * floorArea);
      totalMin += floorPrice.min * floorArea;
      totalMax += floorPrice.max * floorArea;

      // Fixtures
      const bathPrice = SCOPE_SECTIONS.bath.options.find(o => o.id === bathOption)?.price || { min: 0, max: 0 };
      const showerPrice = SCOPE_SECTIONS.shower.options.find(o => o.id === showerOption)?.price || { min: 0, max: 0 };
      const showerSysPrice = SCOPE_SECTIONS.shower.systems.find(s => s.id === showerSystem)?.price || { min: 0, max: 0 };
      const basinPrice = SCOPE_SECTIONS.basin.options.find(o => o.id === basinOption)?.price || { min: 0, max: 0 };
      const toiletPrice = SCOPE_SECTIONS.toilet.options.find(o => o.id === toiletOption)?.price || { min: 0, max: 0 };

      breakdown.fixtures = Math.round(
        (bathPrice.min + bathPrice.max) / 2 +
        (showerPrice.min + showerPrice.max) / 2 +
        (showerSysPrice.min + showerSysPrice.max) / 2 +
        (basinPrice.min + basinPrice.max) / 2 +
        (toiletPrice.min + toiletPrice.max) / 2
      );
      
      totalMin += bathPrice.min + showerPrice.min + showerSysPrice.min + basinPrice.min + toiletPrice.min;
      totalMax += bathPrice.max + showerPrice.max + showerSysPrice.max + basinPrice.max + toiletPrice.max;

      // Plumbing
      const plumbingPrices = { none: { min: 0, max: 0 }, minor: { min: 400, max: 800 }, major: { min: 800, max: 1500 } };
      const plumbingPrice = plumbingPrices[plumbingComplexity] || { min: 0, max: 0 };
      breakdown.plumbing = Math.round((plumbingPrice.min + plumbingPrice.max) / 2);
      totalMin += plumbingPrice.min;
      totalMax += plumbingPrice.max;

      // Selected scope items
      let extrasMin = 0;
      let extrasMax = 0;
      Object.entries(selectedItems).forEach(([category, items]) => {
        const section = SCOPE_SECTIONS[category];
        if (section?.items) {
          Object.entries(items).forEach(([itemId, isSelected]) => {
            if (isSelected) {
              const item = section.items.find(i => i.id === itemId);
              if (item?.price) {
                extrasMin += item.price.min;
                extrasMax += item.price.max;
              }
            }
          });
        }
      });
      breakdown.extras = Math.round((extrasMin + extrasMax) / 2);
      totalMin += extrasMin;
      totalMax += extrasMax;

      // Round to nearest 100
      const estimateLow = Math.round(totalMin / 100) * 100;
      const estimateHigh = Math.round(totalMax / 100) * 100;

      setEstimate({
        estimate_low: estimateLow,
        estimate_high: estimateHigh,
        currency: "GBP",
        breakdown
      });

      // Try to send to backend
      try {
        await axios.post(`${API}/estimate/bathroom-refit`, {
          bathroom_size: bathroomSize,
          wall_area_m2: wallArea,
          floor_area_m2: floorArea,
          tile_type: wallTileType,
          fixtures: [bathOption, showerOption, basinOption, toiletOption].filter(f => f && !f.includes("no_")),
          plumbing_complexity: plumbingComplexity,
          extras: Object.entries(selectedItems).flatMap(([cat, items]) => 
            Object.entries(items).filter(([_, v]) => v).map(([k]) => k)
          )
        });
      } catch (e) {
        // Continue even if backend fails
      }

    } catch (error) {
      console.error("Calculation error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentStep === steps.length - 1 && !estimate) {
      calculateEstimate();
    }
  }, [currentStep]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Layout Selection
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Bathroom Layout</h2>
              <p className="text-gray-600">Select the layout that best matches your bathroom or choose custom</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {BATHROOM_LAYOUTS.map((layout) => (
                <button
                  key={layout.id}
                  onClick={() => handleLayoutSelect(layout)}
                  className={`p-6 rounded-xl border-2 text-left transition-all hover:border-primary hover:shadow-md ${
                    selectedLayout?.id === layout.id ? "border-primary bg-primary/5" : "border-gray-200"
                  }`}
                >
                  <div className="text-3xl mb-3">{layout.icon}</div>
                  <h3 className="font-semibold text-gray-900 mb-1">{layout.name}</h3>
                  <p className="text-sm text-gray-600 mb-2">{layout.description}</p>
                  <p className="text-xs text-primary">Approx. {layout.floorArea.min}-{layout.floorArea.max} m²</p>
                </button>
              ))}
            </div>
          </div>
        );

      case 1: // Bathroom Size
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Bathroom Dimensions</h2>
              <p className="text-gray-600">Adjust the dimensions for your bathroom</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {[
                { id: "small", label: "Small", area: "3-4 m²" },
                { id: "medium", label: "Medium", area: "5-7 m²" },
                { id: "large", label: "Large", area: "8-12 m²" }
              ].map((size) => (
                <button
                  key={size.id}
                  onClick={() => {
                    setBathroomSize(size.id);
                    setFloorArea(size.id === "small" ? 3.5 : size.id === "medium" ? 6 : 10);
                    setWallArea(size.id === "small" ? 14 : size.id === "medium" ? 24 : 40);
                  }}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    bathroomSize === size.id ? "border-primary bg-primary/5" : "border-gray-200"
                  }`}
                >
                  <p className="font-semibold">{size.label}</p>
                  <p className="text-sm text-gray-500">{size.area}</p>
                </button>
              ))}
            </div>

            <div className="bg-gray-50 rounded-xl p-6 space-y-6">
              <div>
                <div className="flex justify-between mb-2">
                  <Label>Floor Area</Label>
                  <span className="font-bold text-primary">{floorArea} m²</span>
                </div>
                <Slider
                  value={[floorArea]}
                  onValueChange={(v) => setFloorArea(v[0])}
                  min={2}
                  max={20}
                  step={0.5}
                />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <Label>Wall Area (approx.)</Label>
                  <span className="font-bold text-primary">{wallArea} m²</span>
                </div>
                <Slider
                  value={[wallArea]}
                  onValueChange={(v) => setWallArea(v[0])}
                  min={8}
                  max={60}
                  step={1}
                />
              </div>
            </div>
          </div>
        );

      case 2: // Removal
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{SCOPE_SECTIONS.removal.title}</h2>
              <p className="text-gray-600">Select what needs to be removed from your existing bathroom</p>
            </div>
            <div className="flex justify-end mb-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => selectAllItems("removal", SCOPE_SECTIONS.removal.items)}
                data-testid="select-all-removal"
              >
                {SCOPE_SECTIONS.removal.items.every(item => selectedItems.removal?.[item.id]) ? "Deselect All" : "Select All"}
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {SCOPE_SECTIONS.removal.items.map((item) => (
                <label
                  key={item.id}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedItems.removal?.[item.id] ? "border-primary bg-primary/5" : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <Checkbox
                    checked={selectedItems.removal?.[item.id] || false}
                    onCheckedChange={() => toggleItem("removal", item.id)}
                  />
                  <span className="flex-1">{item.label}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 3: // Plumbing
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Plumbing Requirements</h2>
              <p className="text-gray-600">What level of plumbing work is needed?</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {[
                { id: "none", label: "No Changes", desc: "Using existing connections" },
                { id: "minor", label: "Minor Adjustments", desc: "Small modifications needed" },
                { id: "major", label: "Major Relocation", desc: "Moving fixtures significantly" }
              ].map((option) => (
                <button
                  key={option.id}
                  onClick={() => setPlumbingComplexity(option.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    plumbingComplexity === option.id ? "border-primary bg-primary/5" : "border-gray-200"
                  }`}
                >
                  <p className="font-semibold">{option.label}</p>
                  <p className="text-sm text-gray-500">{option.desc}</p>
                </button>
              ))}
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">Detailed Plumbing Work</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => selectAllItems("plumbing", SCOPE_SECTIONS.plumbing.items)}
                  data-testid="select-all-plumbing"
                >
                  {SCOPE_SECTIONS.plumbing.items.every(item => selectedItems.plumbing?.[item.id]) ? "Deselect All" : "Select All"}
                </Button>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {SCOPE_SECTIONS.plumbing.items.map((item) => (
                  <label
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedItems.plumbing?.[item.id] ? "border-primary bg-white" : "border-gray-200"
                    }`}
                  >
                    <Checkbox
                      checked={selectedItems.plumbing?.[item.id] || false}
                      onCheckedChange={() => toggleItem("plumbing", item.id)}
                    />
                    <span className="text-sm">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 4: // Electrical
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{SCOPE_SECTIONS.electrical.title}</h2>
              <p className="text-gray-600">Select electrical work required</p>
            </div>
            <div className="flex justify-end mb-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => selectAllItems("electrical", SCOPE_SECTIONS.electrical.items)}
                data-testid="select-all-electrical"
              >
                {SCOPE_SECTIONS.electrical.items.every(item => selectedItems.electrical?.[item.id]) ? "Deselect All" : "Select All"}
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {SCOPE_SECTIONS.electrical.items.map((item) => (
                <label
                  key={item.id}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedItems.electrical?.[item.id] ? "border-primary bg-primary/5" : "border-gray-200"
                  }`}
                >
                  <Checkbox
                    checked={selectedItems.electrical?.[item.id] || false}
                    onCheckedChange={() => toggleItem("electrical", item.id)}
                  />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 5: // Wall Tiling
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Wall Tiling</h2>
              <p className="text-gray-600">Choose your wall tiling coverage and tile type</p>
            </div>
            
            <div className="mb-6">
              <Label className="mb-3 block">Tiling Coverage</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {SCOPE_SECTIONS.wallFinishes.options.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setWallTilingCoverage(option.id)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      wallTilingCoverage === option.id ? "border-primary bg-primary/5" : "border-gray-200"
                    }`}
                  >
                    <p className="font-medium text-sm">{option.label}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <Label className="mb-3 block">Tile Type</Label>
              <div className="grid md:grid-cols-3 gap-3">
                {SCOPE_SECTIONS.wallFinishes.tileTypes.map((tile) => (
                  <button
                    key={tile.id}
                    onClick={() => setWallTileType(tile.id)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      wallTileType === tile.id ? "border-primary bg-primary/5" : "border-gray-200"
                    }`}
                  >
                    <p className="font-medium">{tile.label}</p>
                    <p className="text-sm text-gray-500">£{tile.price.min}-{tile.price.max}/m²</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold mb-4">Additional Wall Work</h3>
              <div className="space-y-3">
                {[...SCOPE_SECTIONS.wallFinishes.extras, ...SCOPE_SECTIONS.waterproofing.items].map((item) => (
                  <label
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedItems.wallFinishes?.[item.id] ? "border-primary bg-white" : "border-gray-200"
                    }`}
                  >
                    <Checkbox
                      checked={selectedItems.wallFinishes?.[item.id] || false}
                      onCheckedChange={() => toggleItem("wallFinishes", item.id)}
                    />
                    <span className="text-sm">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 6: // Floor Finishes
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Floor Finishes</h2>
              <p className="text-gray-600">Choose your floor covering</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-3 mb-6">
              {SCOPE_SECTIONS.floorFinishes.options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setFloorTileType(option.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    floorTileType === option.id ? "border-primary bg-primary/5" : "border-gray-200"
                  }`}
                >
                  <p className="font-medium">{option.label}</p>
                  <p className="text-sm text-gray-500">£{option.price.min}-{option.price.max}/m²</p>
                </button>
              ))}
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold mb-4">Additional Floor Work</h3>
              <div className="space-y-3">
                {SCOPE_SECTIONS.floorFinishes.extras.map((item) => (
                  <label
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedItems.floorFinishes?.[item.id] ? "border-primary bg-white" : "border-gray-200"
                    }`}
                  >
                    <Checkbox
                      checked={selectedItems.floorFinishes?.[item.id] || false}
                      onCheckedChange={() => toggleItem("floorFinishes", item.id)}
                    />
                    <span className="text-sm">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 7: // Fixtures
        return (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Bathroom Fixtures</h2>
              <p className="text-gray-600">Select your bath, shower, basin and toilet</p>
            </div>
            
            {/* Bath */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Bath className="w-5 h-5 text-primary" />
                Bath Installation
              </h3>
              <div className="grid md:grid-cols-3 gap-3">
                {SCOPE_SECTIONS.bath.options.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setBathOption(option.id)}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      bathOption === option.id ? "border-primary bg-primary/5" : "border-gray-200"
                    }`}
                  >
                    <p className="font-medium text-sm">{option.label}</p>
                    {option.price.max > 0 && (
                      <p className="text-xs text-gray-500">£{option.price.min}-{option.price.max}</p>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Shower */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <ShowerHead className="w-5 h-5 text-primary" />
                Shower Installation
              </h3>
              <div className="grid md:grid-cols-2 gap-3 mb-3">
                {SCOPE_SECTIONS.shower.options.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setShowerOption(option.id)}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      showerOption === option.id ? "border-primary bg-primary/5" : "border-gray-200"
                    }`}
                  >
                    <p className="font-medium text-sm">{option.label}</p>
                    {option.price.max > 0 && (
                      <p className="text-xs text-gray-500">£{option.price.min}-{option.price.max}</p>
                    )}
                  </button>
                ))}
              </div>
              {showerOption !== "no_shower" && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium mb-2">Shower System</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {SCOPE_SECTIONS.shower.systems.map((system) => (
                      <button
                        key={system.id}
                        onClick={() => setShowerSystem(system.id)}
                        className={`p-2 rounded border text-xs transition-all ${
                          showerSystem === system.id ? "border-primary bg-white" : "border-gray-200"
                        }`}
                      >
                        {system.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Basin */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <CircleDot className="w-5 h-5 text-primary" />
                Basin Installation
              </h3>
              <div className="grid md:grid-cols-3 gap-3">
                {SCOPE_SECTIONS.basin.options.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setBasinOption(option.id)}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      basinOption === option.id ? "border-primary bg-primary/5" : "border-gray-200"
                    }`}
                  >
                    <p className="font-medium text-sm">{option.label}</p>
                    <p className="text-xs text-gray-500">£{option.price.min}-{option.price.max}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Toilet */}
            <div>
              <h3 className="font-semibold mb-3">Toilet Installation</h3>
              <div className="grid md:grid-cols-3 gap-3">
                {SCOPE_SECTIONS.toilet.options.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setToiletOption(option.id)}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      toiletOption === option.id ? "border-primary bg-primary/5" : "border-gray-200"
                    }`}
                  >
                    <p className="font-medium text-sm">{option.label}</p>
                    <p className="text-xs text-gray-500">£{option.price.min}-{option.price.max}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 8: // Extras
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Additional Features</h2>
              <p className="text-gray-600">Select any extras for your bathroom</p>
            </div>
            
            {/* Storage */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold mb-4">{SCOPE_SECTIONS.storage.title}</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {SCOPE_SECTIONS.storage.items.map((item) => (
                  <label
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer bg-white transition-all ${
                      selectedItems.storage?.[item.id] ? "border-primary" : "border-gray-200"
                    }`}
                  >
                    <Checkbox
                      checked={selectedItems.storage?.[item.id] || false}
                      onCheckedChange={() => toggleItem("storage", item.id)}
                    />
                    <span className="text-sm">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Heating */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold mb-4">{SCOPE_SECTIONS.heating.title}</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {SCOPE_SECTIONS.heating.items.map((item) => (
                  <label
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer bg-white transition-all ${
                      selectedItems.heating?.[item.id] ? "border-primary" : "border-gray-200"
                    }`}
                  >
                    <Checkbox
                      checked={selectedItems.heating?.[item.id] || false}
                      onCheckedChange={() => toggleItem("heating", item.id)}
                    />
                    <span className="text-sm">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Optional Extras */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-semibold mb-4">{SCOPE_SECTIONS.extras.title}</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {SCOPE_SECTIONS.extras.items.map((item) => (
                  <label
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer bg-white transition-all ${
                      selectedItems.extras?.[item.id] ? "border-primary" : "border-gray-200"
                    }`}
                  >
                    <Checkbox
                      checked={selectedItems.extras?.[item.id] || false}
                      onCheckedChange={() => toggleItem("extras", item.id)}
                    />
                    <span className="text-sm">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 9: // Photos
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Photos</h2>
              <p className="text-gray-600">Share photos of your bathroom to help us provide an accurate estimate</p>
            </div>
            
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
              <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600 mb-4">Drag photos here or click to upload</p>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
                id="photo-upload"
              />
              <label htmlFor="photo-upload">
                <Button variant="outline" className="cursor-pointer" asChild>
                  <span>Choose Photos</span>
                </Button>
              </label>
              <p className="text-xs text-gray-500 mt-2">Maximum 5 photos, 5MB each</p>
            </div>

            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-4">
                {photos.map((photo, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden">
                    <img src={photo.data} alt={photo.name} className="w-full h-full object-cover" />
                    <button
                      onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <Button onClick={() => setCurrentStep(10)} className="w-full">
              {photos.length > 0 ? "Continue" : "Skip This Step"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 10: // Postcode
        return (
          <div className="space-y-6 max-w-md mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Location</h2>
              <p className="text-gray-600">Enter your postcode to see if we cover your area</p>
            </div>
            
            <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
              <MapPin className="w-5 h-5 flex-shrink-0" />
              <p>We are based in Greater Manchester but may travel depending on the project.</p>
            </div>

            <div>
              <Label>Your Postcode</Label>
              <Input
                value={postcode}
                onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                placeholder="e.g. M1 1AA"
                className="mt-1 text-center text-lg"
              />
            </div>

            <Button 
              onClick={() => setCurrentStep(11)} 
              className="w-full"
              disabled={!postcode}
            >
              Get My Estimate
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 11: // Estimate
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Bathroom Renovation Estimate</h2>
              <p className="text-gray-600">Based on your selections</p>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
                <p className="text-gray-600">Calculating your estimate...</p>
              </div>
            ) : estimate ? (
              <>
                <div className="bg-gradient-to-br from-primary to-blue-700 rounded-2xl p-8 text-center text-white">
                  <p className="text-white/80 mb-2">Estimated Bathroom Renovation Cost</p>
                  <p className="text-5xl font-bold mb-2">
                    £{estimate.estimate_low.toLocaleString()} – £{estimate.estimate_high.toLocaleString()}
                  </p>
                  <p className="text-white/60 text-sm">Subject to site survey</p>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-semibold mb-4">Cost Breakdown</h3>
                  <div className="space-y-3">
                    {Object.entries(estimate.breakdown).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-gray-600 capitalize">{key.replace(/_/g, " ")}</span>
                        <span className="font-medium">£{value.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-amber-50 rounded-lg p-4 flex items-start gap-3">
                  <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    This is an estimate based on your selections. Final pricing will be confirmed after a site visit.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <Link to="/contact">
                    <Button className="w-full bg-primary hover:bg-primary/90 py-6">
                      <Calendar className="w-5 h-5 mr-2" />
                      Book Quote Appointment
                    </Button>
                  </Link>
                  <Link to="/finance">
                    <Button variant="outline" className="w-full py-6">
                      <Calculator className="w-5 h-5 mr-2" />
                      Calculate Finance Payments
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600">Unable to calculate estimate. Please try again.</p>
                <Button onClick={calculateEstimate} className="mt-4">
                  Retry Calculation
                </Button>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div data-testid="bathroom-refit-page">
      {/* Hero */}
      <section className="py-12 bg-gray-50 grid-pattern">
        <div className="container-custom text-center">
          <div ref={pageTopRef} id="bathroom-refit-top" className="absolute -top-20" />
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full mb-4">
            <Bath className="w-4 h-4" />
            <span className="text-sm font-medium">Bathroom Renovation Planner</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
            Plan Your Bathroom Renovation
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {estimatorMode === "simple" 
              ? "Get a quick estimate with just a few questions" 
              : "Get a detailed estimate by selecting exactly what you need for your project"}
          </p>
        </div>
      </section>

      {/* Tab Selector */}
      <section className="py-4 bg-white border-b border-gray-100">
        <div className="container-custom">
          <div className="flex justify-center">
            <div className="inline-flex bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => {
                  setEstimatorMode("simple");
                  setSimpleStep(0);
                  scrollToPageTop();
                }}
                className={`px-6 py-3 rounded-lg font-medium text-sm transition-all ${
                  estimatorMode === "simple"
                    ? "bg-white text-primary shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                data-testid="tab-simple-quote"
              >
                <Calculator className="w-4 h-4 inline mr-2" />
                Simple Quote
              </button>
              <button
                onClick={() => {
                  setEstimatorMode("detailed");
                  setCurrentStep(0);
                  scrollToPageTop();
                }}
                className={`px-6 py-3 rounded-lg font-medium text-sm transition-all ${
                  estimatorMode === "detailed"
                    ? "bg-white text-primary shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                data-testid="tab-detailed-quote"
              >
                <Layers className="w-4 h-4 inline mr-2" />
                Detailed Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Conditional Content */}
      {estimatorMode === "simple" ? (
        <section className="section-padding">
          <div className="container-custom">
            {renderSimpleQuote()}
          </div>
        </section>
      ) : (
        <>
          {/* Progress - only show for detailed */}
          <section className="py-6 bg-white border-b border-gray-100 sticky top-20 z-30">
            <div className="container-custom">
              <div className="flex items-center justify-between max-w-4xl mx-auto overflow-x-auto">
                {steps.map((step, i) => (
                  <div 
                    key={i} 
                    className={`flex flex-col items-center min-w-[80px] ${
                      i <= currentStep ? "text-primary" : "text-gray-400"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mb-1 ${
                      i < currentStep ? "bg-primary text-white" : i === currentStep ? "bg-primary/20 text-primary border-2 border-primary" : "bg-gray-100"
                    }`}>
                      {i < currentStep ? <Check className="w-4 h-4" /> : i + 1}
                    </div>
                    <span className="text-xs text-center hidden md:block">{step.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Main Content */}
          <section className="section-padding">
            <div className="container-custom">
              <div className="max-w-4xl mx-auto">
                {renderStepContent()}

                {/* Navigation */}
                {currentStep > 0 && currentStep < steps.length - 1 && (
                  <div className="flex justify-between mt-8 pt-8 border-t border-gray-200">
                    <Button
                      variant="outline"
                      onClick={() => {
                        scrollToPageTop();
                        setCurrentStep(prev => prev - 1);
                      }}
                      data-testid="back-btn"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <Button
                      onClick={() => {
                        scrollToPageTop();
                        setCurrentStep(prev => prev + 1);
                      }}
                      className="bg-primary hover:bg-primary/90"
                      data-testid="continue-btn"
                    >
                      Continue
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default BathroomRefitPage;
